import { isUserVip } from "../vip/isUserVip";

export const idsMatch = (a, b) =>
  a != null && b != null && String(a) === String(b);

export const getReactionUserId = (reaction) =>
  reaction?.userId || reaction?.user?._id || reaction?._id || null;

export const isReactionVip = (reaction) =>
  isUserVip(reaction) || isUserVip(reaction?.user);

export const getMyReactionEmoji = (reactions = [], userId) => {
  if (!userId) return null;
  const mine = reactions.find((reaction) =>
    idsMatch(getReactionUserId(reaction), userId)
  );
  return mine?.emoji || null;
};

export const groupReactions = (reactions = []) => {
  const groups = [];
  const byEmoji = new Map();

  reactions.forEach((reaction) => {
    const emoji = reaction?.emoji;
    if (!emoji) return;

    if (!byEmoji.has(emoji)) {
      const group = {
        emoji,
        count: 0,
        users: [],
        hasVip: false,
      };
      byEmoji.set(emoji, group);
      groups.push(group);
    }

    const group = byEmoji.get(emoji);
    group.count += 1;
    group.users.push(reaction);
    if (isReactionVip(reaction)) group.hasVip = true;
  });

  return groups;
};

export const toggleReactionLocally = (reactions = [], user, emoji) => {
  const userId = user?._id || user?.id;
  if (!userId || !emoji) return reactions;

  const withoutMine = reactions.filter(
    (reaction) => !idsMatch(getReactionUserId(reaction), userId)
  );
  const mine = reactions.find((reaction) =>
    idsMatch(getReactionUserId(reaction), userId)
  );

  if (mine?.emoji === emoji) return withoutMine;

  return [
    ...withoutMine,
    {
      userId,
      userReference: "User",
      userName: user?.userName || user?.name || "",
      avatarUrl: user?.avatarUrl || user?.profile_pic || "",
      isVip: isUserVip(user),
      emoji,
      reactedAt: new Date().toISOString(),
    },
  ];
};

export const patchMessageReactions = (messages = [], messageId, reactions) => {
  if (!messageId) return messages;
  return messages.map((msg) =>
    idsMatch(msg?._id, messageId) ? { ...msg, reactions } : msg
  );
};

const SKIN_TONE_UNIFIEDS = ["1f3fb", "1f3fc", "1f3fd", "1f3fe", "1f3ff"];
const SKIN_TONE_OR_VS16 = /[\u{1F3FB}-\u{1F3FF}]|\uFE0F/gu;

// Drops skin tones and the emoji presentation selector so "🖕🏽" matches "🖕".
export const normalizeEmoji = (emoji = "") =>
  String(emoji).replace(SKIN_TONE_OR_VS16, "");

export const isEmojiBlocked = (emoji, blockedEmojis = []) => {
  const normalized = normalizeEmoji(emoji);
  if (!normalized) return false;
  return blockedEmojis.some((blocked) => normalizeEmoji(blocked) === normalized);
};

const toUnified = (emoji = "") =>
  Array.from(emoji)
    .map((char) => char.codePointAt(0).toString(16))
    .join("-");

// Unified codes (as emoji-picker-react expects in `hiddenEmojis`) for every
// blocked emoji, with and without VS16, plus all skin-tone variants.
export const getHiddenEmojiUnifieds = (blockedEmojis = []) => {
  const unifieds = new Set();
  blockedEmojis.forEach((emoji) => {
    [toUnified(emoji), toUnified(normalizeEmoji(emoji))].forEach((unified) => {
      if (!unified) return;
      unifieds.add(unified);
      SKIN_TONE_UNIFIEDS.forEach((tone) => unifieds.add(`${unified}-${tone}`));
    });
  });
  return [...unifieds];
};

export const parseSocketErrorMessage = (err) => {
  if (!err) return "";
  if (typeof err === "string") return err;
  return (
    err.message ||
    err.msg ||
    err.error ||
    err.data?.message ||
    ""
  );
};
